import React, { useEffect, useRef, useState } from "react";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import UploadIcon from "@mui/icons-material/Upload";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import ResponsiveGrid from "../../grid/responsiveGrid";
import InfrastructureDialog from "./infrastructureDialog";
import InfrastructureMap from "./infrastructureMap";

import { GridActionsCellItem, GridToolbarContainer } from "@mui/x-data-grid";
import { useUserAuth } from "../../contexts/authContext";
import { CustomPagination } from "../../customPagination";
import { ParseInfrastructureFile } from "./infrastructureUtil";
import { ConvertISOToDate } from "../../util";

export function InfrastructureDashboard() {
    const [infrastructureAssets, setInfrastructureAssets] = useState([]);
    const [infrastructureAssetsSelected, setInfrastructureAssetsSelected] = useState([]);
    const [infrastructureTypes, setInfrastructureTypes] = useState([]);
    const [infrastructureDialogOpen, setInfrastructureDialogOpen] = useState(false);
    const [editRow, setEditRow] = useState({});
    const [sortModel, setSortModel] = useState([{ field: "name", sort: "asc" }]);
    const { handleFailedFetch } = useUserAuth();

    const infrastructureColumns = [
        {
            field: "name",
            headerName: "Name",
            width: 100,
            editable: false,
            flex: 1
        },
        {
            field: "type_id",
            headerName: "Type",
            width: 100,
            editable: false,
            flex: 1,
            renderCell: (params) => {
                const type = infrastructureTypes.find((type) => type.id === params.value);
                return type ? type.name : "Unknown";
            }
        },
        {
            field: "assessment",
            headerName: "Assessment",
            width: 150,
            editable: false,
            valueFormatter: (value) => (value ? "Assessed" : "Not Assessed")
        },
        {
            field: "description",
            headerName: "Description",
            width: 100,
            editable: false,
            flex: 1
        },
        {
            field: "date_created",
            headerName: "Date Created",
            width: 100,
            editable: false,
            flex: 1,
            type: "dateTime",
            valueFormatter: (value) => ConvertISOToDate(value)
        },
        {
            field: "actions",
            type: "actions",
            headerName: "Actions",
            width: 100,
            getActions: (params) => {
                return [
                    <GridActionsCellItem
                        key={params.id}
                        icon={<EditIcon />}
                        label="Edit"
                        className="textPrimary"
                        onClick={() => handleEditClick(params.row)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        key={params.id}
                        icon={<DeleteIcon />}
                        label="Delete"
                        className="textPrimary"
                        onClick={() => handleDeleteClick(params.row)}
                        color="inherit"
                        id={`delete-${params.row.volumeId}`}
                    />
                ];
            }
        }
    ];

    const getInfrastructureAssets = async () => {
        const setupVolumeIds = (volumes) => {
            const volumeCount = {};
            return volumes.map((volume) => {
                const name = volume.name;
                const count = volumeCount[name] ? volumeCount[name] + 1 : 1;
                volumeCount[name] = count;
                return {
                    ...volume,
                    volumeId: `${name}-${count}`
                };
            });
        };
        const requestOptions = {
            method: "GET",
            headers: { "Content-Type": "application/json" }
        };
        await fetch("/api/infrastructure/assets/get", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then((data) => setInfrastructureAssets(setupVolumeIds(data)))
            .catch((err) => handleFailedFetch(err));
    };

    useEffect(() => {
        const getInfrastructureTypes = async () => {
            const requestOptions = {
                method: "GET",
                headers: { "Content-Type": "application/json" }
            };
            await fetch("/api/infrastructure/types/get", requestOptions)
                .then((response) => (response.ok ? response.json() : Promise.reject(response)))
                .then((data) => setInfrastructureTypes(data))
                .catch((err) => handleFailedFetch(err));
        };
        getInfrastructureTypes();
        getInfrastructureAssets();
    }, []);

    const handleEditClick = (row) => {
        setEditRow(row);
        setInfrastructureDialogOpen(true);
    };

    const handleDeleteClick = (row) => {
        if (!confirm("Are you sure you want to Delete this Infrastructure?")) {
            return;
        }
        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" }
        };
        fetch(`/api/infrastructure/assets/delete/${row.id}`, requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then(() => getInfrastructureAssets())
            .catch((err) => handleFailedFetch(err));
    };

    const handleClose = () => {
        setInfrastructureDialogOpen(false);
        getInfrastructureAssets();
    };

    const handleCheckboxSelection = (ids) => {
        const selected = infrastructureAssets.filter((asset) => ids.includes(asset.id));
        setInfrastructureAssetsSelected(selected.length > 0 ? selected : []);
    };

    return (
        <Grid sx={{ maxWidth: "100% !important", direction: "column", justifyContent: "flex-start", alignItems: "flex-start", p: 3, width: "100%" }}>
            <Typography component="h2" variant="h6" color="#6b778c" noWrap sx={{ flexGrow: 1 }}>
                Infrastructure Dashboard
            </Typography>
            <Paper sx={{ p: 2, mt: 2, display: "flex", flexDirection: "column", width: "100%" }}>
                <ResponsiveGrid
                    primaryColumns={["name", "actions"]}
                    rows={infrastructureAssets}
                    columns={infrastructureColumns}
                    disableRowSelectionOnClick
                    disableVirtualization
                    autoHeight
                    checkboxSelection
                    getRowId={(row) => row.id}
                    onRowSelectionModelChange={handleCheckboxSelection}
                    slots={{ toolbar: UploadToolbar, pagination: CustomPagination }}
                    slotProps={{ toolbar: { infrastructureTypes, getInfrastructureAssets } }}
                    initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                    sortModel={sortModel}
                    onSortModelChange={(model) => setSortModel(model)}
                />
                {infrastructureDialogOpen && (
                    <InfrastructureDialog
                        row={editRow}
                        infrastructureTypes={infrastructureTypes}
                        infrastructureDialogOpen={infrastructureDialogOpen}
                        handleClose={handleClose}
                    />
                )}
                <Paper sx={{ height: "600px", p: 2 }}>
                    <InfrastructureMap assets={infrastructureAssets} selectedAssets={infrastructureAssetsSelected} infrastructureTypes={infrastructureTypes} />
                </Paper>
            </Paper>
        </Grid>
    );
}

function UploadToolbar(props) {
    const uploadRef = useRef(null);
    const { setSnackbar, handleFailedFetch } = useUserAuth();

    const handleUpload = (files) => {
        ParseInfrastructureFile(files[0])
            .then((features) => handleInserts(features))
            .catch((error) => setSnackbar({ children: error, severity: "error" }));
    };

    const handleInserts = async (features) => {
        const seen = new Set();
        for (const feature of features) {
            if (!feature.get("coords") || !feature.get("name") || seen.has(feature.get("name"))) {
                continue;
            }

            seen.add(feature.get("name"));

            const type = props.infrastructureTypes.find((type) => type.name === feature.get("type"));
            const infrastructureAsset = {
                id: 0,
                coordinates: JSON.stringify(feature.get("coords")),
                name: feature.get("name"),
                type_id: type ? type.id : -1,
                assessment: feature.get("assessment"),
                description: feature.get("description") || ""
            };

            const requestOptions = {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(infrastructureAsset)
            };
            await fetch("/api/infrastructure/assets/insertUpdate", requestOptions).catch((err) => handleFailedFetch(err));
        }

        props.getInfrastructureAssets();
        setSnackbar({ children: `${seen.size} assets have been successfully uploaded.`, severity: "success" });
    };

    return (
        <GridToolbarContainer>
            <Button color="primary" startIcon={<UploadIcon />} onClick={() => uploadRef.current.click()} id="uploadAssets">
                Upload Assets
            </Button>
            <input
                type="file"
                ref={uploadRef}
                style={{ display: "none" }}
                onChange={(e) => handleUpload(e.target.files)}
                onClick={(e) => (e.target.value = "")}
                aria-label="file uploader"
                id="fileUpload"
            />
        </GridToolbarContainer>
    );
}
